/**
 * Hands a generated file to the person (TNG-96).
 *
 * An installed iOS PWA has no download manager: a link with `download` opens
 * the file in a view with no way back into the app. The share sheet is how a
 * file leaves an iOS app, so it is used on iOS and on other touch-first
 * devices that can share files. Desktop Safari, Chrome and Edge can share
 * files too, but there a share sheet is not what a person expects from a PDF
 * button, so they download. The caller also keeps a Download link on screen,
 * for the person who closes the share sheet and still wants the file, except
 * on iOS, where that link would open the same trap, and a "Share again"
 * button takes its place.
 *
 * The share sheet only opens inside a tap. Building the PDF awaits Firestore
 * and two font files, and Safari may no longer count the tap by the time it
 * is ready; it then refuses with NotAllowedError. That comes back as
 * "needs-tap", and the caller offers a second button that shares the file
 * already built, from a fresh tap.
 */

export type Delivery = "shared" | "downloaded" | "cancelled" | "needs-tap";

/** iOS or iPadOS; an iPad reports itself as a Mac with touch. */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const agent = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(agent) ||
    (/Macintosh/.test(agent) && navigator.maxTouchPoints > 1)
  );
}

/** iOS, or a coarse pointer. */
export function prefersShareSheet(): boolean {
  if (typeof navigator === "undefined" || typeof window === "undefined")
    return false;
  const coarse =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  return isIOS() || coarse;
}

function canShareFile(file: File): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  );
}

function download(file: File): void {
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked later rather than at once: some browsers start reading the blob
  // only after the click handler returns.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function deliverFile(
  blob: Blob,
  name: string,
  title: string,
): Promise<Delivery> {
  const file = new File([blob], name, { type: blob.type || "application/pdf" });
  if (!prefersShareSheet() || !canShareFile(file)) {
    download(file);
    return "downloaded";
  }
  try {
    await navigator.share({ files: [file], title });
    return "shared";
  } catch (error) {
    // A DOMException, which is not an Error in every engine.
    const reason =
      typeof error === "object" && error !== null && "name" in error
        ? String((error as { name: unknown }).name)
        : "";
    if (reason === "AbortError") return "cancelled";
    if (reason === "NotAllowedError") return "needs-tap";
    // Any other refusal (a share target that failed) still leaves the person
    // wanting the file.
    download(file);
    return "downloaded";
  }
}
